import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { catchError, forkJoin, map, of, Subscription } from 'rxjs';

import { saveAs } from 'file-saver';
import { DialogTypeEnum, IDialogConfirm } from 'src/app/models/dialog';
import { DetailLotEnum, IContractLot, ILot } from 'src/app/models/generic';
import { IContractIds, IManualBatchLiberacao } from 'src/app/models/manual-batch';
import { ServiceCodeLookup } from 'src/app/models/service-code';
import { ApiCommissionService } from 'src/app/services/api/api-commission.service';
import { ApiLotDetailService } from 'src/app/services/api/api-lot-detail.service';
import { ApiLotsService } from 'src/app/services/api/api-lots.service';
import { ApiVerificationService } from 'src/app/services/api/api-verification.service';
import { CodigosServicoService } from 'src/app/services/codigos-servico.service';
import { DataService } from 'src/app/services/data.service';
import { ContractList, TransferTitle } from 'src/app/shared/utils/base';
import { DocumentValidator } from 'src/app/shared/validators/document.validator';
import { batchStatusTone } from 'src/app/shared/utils/status-badge.util';

import { ApiDialogService } from '../../services/api/api-cdialog.service';
import { ApiManualBatchService } from '../../services/api/api-manual-batch.service';
import { LoaderService } from '../../services/loader.service';
import { SnackbarService } from '../../services/snackbar.service';
import { ModalMsgRpaV2Component } from './modal-msg-rpa-v2/modal-msg-rpa-v2.component';
import {
  IModalPreviewEmailData,
  ModalPreviewEmailV2Component
} from './modal-preview-email-v2/modal-preview-email-v2.component';

@Component({
  selector: 'app-detalhes-lotes-manual-v2',
  templateUrl: './detalhes-lotes-manual-v2.component.html',
  styleUrls: ['./detalhes-lotes-manual-v2.component.scss']
})
export class DetalhesLotesManualV2Component implements OnInit, OnDestroy {
  batch!: ILot;
  isLoading: boolean = true;
  loadError: string | null = null;
  issUpdating: boolean = false;
  typeDetailLot: DetailLotEnum = DetailLotEnum.Transfer;
  contracts = ContractList;
  batchId!: string;
  contractsToExclude: string[] = [];
  integratedOnSAP: boolean = false;
  allowIntegrationSap: boolean = false;
  allowLiberacaoManual: boolean = false;
  allowReintegrationSap: boolean = false;
  allowRefreshNFENumber: boolean = false;
  allowCancelBatch: boolean = false;
  allowReturnStatus: boolean = false;
  showRpaButton: boolean = false;
  disableSendTransferBatchEmail: boolean = false;
  invoiceChangeSubscription!: Subscription;
  rpaChangeSubscription!: Subscription;
  batchBlocked: boolean = false;
  integratedSubestabelecidoContractIds: Set<string> = new Set();
  selectedContractIdsToExclude: Set<string> = new Set();
  observacao: FormControl<string> = new FormControl('', { nonNullable: true });
  productsById: Record<string, string> = {};
  serviceCodes: ServiceCodeLookup[] = [];
  // Sublotes cujas retenções estão sendo recalculadas pelo servidor: só o bloco de retenções deles vira skeleton.
  private recalculatingBatchIds = new Set<string>();

  get batchTotalAmount(): number {
    return (this.batch?.Partners || []).reduce((sum, partner) => {
      const contracts = (partner.Contracts || [])
        .filter(contract => !contract.IsSubestabelecidoContract)
        .reduce((total, contract) => total + (contract.Amount || 0), 0);
      const reverses = (partner.Reverses || [])
        .filter(contract => !contract.IsSubestabelecidoContract)
        .reduce((total, contract) => total + (contract.Amount || 0), 0);

      return sum + contracts - reverses;
    }, 0);
  }

  get batchTotalTransferAmount(): number {
    return this.batch?.AmountTotal || 0;
  }

  get batchContractsCount(): number {
    return this.batch?.NumberOfContracts || 0;
  }

  // Mantém os cards no DOM quando o lote é recarregado, para a atualização não recriar a tela.
  trackByPartner(_: number, partner: ILot): string {
    return partner.BatchId.toString();
  }

  get masterPartner(): ILot | undefined {
    return this.batch?.Partners?.find(p => !p.FirstContractIsSubstabelecido);
  }

  get substabelecidoPartners(): ILot[] {
    return this.batch?.Partners?.filter(p => p.FirstContractIsSubstabelecido) ?? [];
  }

  get masterPartnersCount(): number {
    return this.batch?.Partners?.filter(p => !p.FirstContractIsSubstabelecido).length ?? 0;
  }

  get substabelecidoPartnersCount(): number {
    return this.batch?.Partners?.filter(p => p.FirstContractIsSubstabelecido).length ?? 0;
  }

  constructor(
    private loaderService: LoaderService,
    private _route: ActivatedRoute,
    private _dialogService: ApiDialogService,
    private _batchService: ApiLotsService,
    private _snackbarService: SnackbarService,
    private _manualBatchService: ApiManualBatchService,
    private _lotDetailService: ApiLotDetailService,
    private _dialog: MatDialog,
    private _dataService: DataService,
    private _commissionService: ApiCommissionService,
    private _verificationService: ApiVerificationService,
    private _codigosServicoService: CodigosServicoService,
  ) {
    this._dataService.setHeaderTitle(TransferTitle);
  }

  ngOnInit(): void {
    this.batchId = this._route.snapshot.params['id'];
    this.loadProductsLookup();
    this.loadServiceCodes();

    if (this.batchId) {
      this.getBatchDetails();
    }

    this.listenInvoiceChange();
  }

  // Carregado uma vez aqui e repassado aos cards, em vez de uma requisição por sublote.
  private loadServiceCodes(): void {
    this._codigosServicoService.getActive().subscribe({
      next: (codes) => (this.serviceCodes = codes),
      error: () => this._snackbarService.openSnackBar('Erro ao carregar os códigos de serviço.', 'danger', 5000)
    });
  }

  private loadProductsLookup(): void {
    this._commissionService.getParametersCommission().subscribe({
      next: (response) => {
        const products = response?.Data?.ProductsName || [];
        this.productsById = products.reduce((acc, product) => {
          const id = (product?.ProductId || '').toString().trim();
          const name = (product?.Name || '').toString().trim();

          if (id && name) {
            acc[id] = name;
            acc[id.toLowerCase()] = name;
          }

          return acc;
        }, {} as Record<string, string>);
      },
      error: () => {
        this.productsById = {};
      }
    });
  }

  ngOnDestroy(): void {
    this.invoiceChangeSubscription?.unsubscribe();
    this.rpaChangeSubscription?.unsubscribe();
  }

  private getObservationValue(batch?: ILot): string {
    return batch?.Observacao ?? batch?.Comment ?? '';
  }

  private syncObservationControl(): void {
    this.observacao.setValue(this.getObservationValue(this.batch), { emitEvent: false });
  }

  saveObservation(): void {
    const comment = this.observacao.value.trim();

    this._lotDetailService.updateCommentTransfer(this.batchId, { Comment: comment }).subscribe({
      next: () => {
        if (this.batch) {
          this.batch = { ...this.batch, Observacao: comment, Comment: comment };
          this.syncObservationControl();
        }

        this._snackbarService.openSnackBar('Observação salva com sucesso!', 'success', 3000);
      },
      error: (error) => {
        const message = error?.error?.Message || 'Erro ao salvar observação. Por favor, tente novamente.';
        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  isRecalculating(partner: ILot | undefined): boolean {
    return !!partner && this.recalculatingBatchIds.has(partner.BatchId.toString());
  }

  // silent: recarrega os dados em segundo plano, sem trocar a página pelo skeleton nem acionar o overlay global.
  // Usado quando só os valores calculados mudam (troca de código de serviço, toggle de ISS). Se a recarga
  // silenciosa falhar, a tela não tem como saber o que é válido e é recarregada por inteiro.
  getBatchDetails(silent: boolean = false): void {
    if (!silent) {
      this.isLoading = true;
    }
    this.loadError = null;

    this._batchService.getLotTransfersById(this.batchId, true, true, !silent).subscribe({
      next: (response) => {
        if (!response?.Data) {
          if (silent) {
            window.location.reload();
            return;
          }

          this.batch = undefined as unknown as ILot;
          this.syncObservationControl();
          this.loadError = 'Não foi possível carregar os detalhes deste lote.';
          this.isLoading = false;
          return;
        }

        this.batch = response.Data;
        this.hydrateContractsProduct(this.batch);
        this.syncObservationControl();
        this.checkIntegrationSap();
        this.checkBatchStatus();
        this.updateSubheader();
        this.isLoading = false;
        this.recalculatingBatchIds.clear();
      },
      error: (error) => {
        if (silent) {
          window.location.reload();
          return;
        }

        this.batch = undefined as unknown as ILot;
        this.syncObservationControl();
        this.isLoading = false;
        this.loadError = error?.error?.Message || error?.message || 'Não foi possível carregar os detalhes deste lote.';
      }
    });
  }

  private updateSubheader(): void {
    if (!this.batch) {
      return;
    }

    const batchNumber = this.batch.BatchNumber || this.batch.NumberCurrentBatch || '';

    this._dataService.setSubheaderOverride({
      title: `Lote ${batchNumber}`.trim(),
      badgeLabel: this.batch.StatusOfOnlending || '-',
      badgeTone: batchStatusTone(this.batch.StatusOfOnlending, { integrationError: !!this.batch.IntegrationError }),
      backLink: '/repasse-comissao'
    });

    this._dataService.setHeaderSummary([
      { label: 'Contratos', value: this.batchContractsCount },
      {
        label: 'Valor Repasse',
        value: this.batchTotalTransferAmount.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
      }
    ]);
  }

  private hydrateContractsProduct(batch: ILot): void {
    const allContracts = (batch.Partners || []).flatMap((partner) => [
      ...(partner.Contracts || []),
      ...(partner.Reverses || [])
    ]);

    const uniqueContracts = new Map<string, IContractLot>();

    allContracts.forEach((contract) => {
      const number = (contract.Number || '').trim();
      const query = (contract.QueryNumber || '').trim();
      const bank = (contract.FinancialInstitution || '').trim();

      if (!number || !query || !bank) {
        return;
      }

      const key = `${number}|${query}|${bank.toLowerCase()}`;
      if (!uniqueContracts.has(key)) {
        uniqueContracts.set(key, contract);
      }
    });

    if (uniqueContracts.size === 0) {
      return;
    }

    const requests = Array.from(uniqueContracts.entries()).map(([key, contract]) =>
      this._verificationService
        .getContractByNumber(contract.Number, contract.QueryNumber, contract.FinancialInstitution)
        .pipe(
          map((result) => ({
            key,
            product: (result?.Data as any)?.Product?.toString?.().trim?.() || ''
          })),
          catchError(() => of({ key, product: '' }))
        )
    );

    forkJoin(requests).subscribe((responses) => {
      const productByKey = responses.reduce((acc, item) => {
        if (item.product) {
          acc[item.key] = item.product;
        }

        return acc;
      }, {} as Record<string, string>);

      if (Object.keys(productByKey).length === 0) {
        return;
      }

      allContracts.forEach((contract) => {
        const number = (contract.Number || '').trim();
        const query = (contract.QueryNumber || '').trim();
        const bank = (contract.FinancialInstitution || '').trim().toLowerCase();
        const key = `${number}|${query}|${bank}`;
        const product = productByKey[key];

        if (product) {
          contract.Product = product;

          if (!contract.ProductName || !contract.ProductName.trim()) {
            contract.ProductName = product;
          }
        }
      });

      this.batch = { ...this.batch };
    });
  }

  openContractListDialog(): void {
    this._dialogService.openManualContractList(this.batch).then((batch) => {
      if (batch) {
        if (batch.SucessOperation == false) {
          this._snackbarService.openSnackBar(
            'Não foi possível adicionar/remover todos os contratos solicitados!',
            'danger',
            3000
          );
        } else {
          this._snackbarService.openSnackBar('Contratos adicionados com sucesso!', 'success', 3000);
        }
        this.batch = { ...batch };
        this.syncObservationControl();
        this.checkIntegrationSap();
        this.checkBatchStatus();
        this.updateSubheader();
      }
    });
  }

  generateRPA(): void {
    const loadingId: string = this.loaderService.show();
    const bathId = this.batch.BatchId.toString();
    this._batchService.generateRpaFile(bathId).subscribe({
      next: (response) => {
        this.loaderService.hide(loadingId);
        const blob = new Blob([response], { type: 'application/pdf' });
        saveAs(blob, `RPA-${bathId}.pdf`);
      },
      error: (error) => {
        const message: string = error.message.includes('404 OK')
          ? 'Parceiro não encontrado no CredIntegrados'
          : 'Houve um erro ao baixar o arquivo';
        this.loaderService.hide(loadingId);
        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  onCalcularIssChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const newValue = checkbox.checked;
    const previousValue = this.batch.CalcularIss;
    const batchId = this.batch.BatchId.toString();

    this.batch = { ...this.batch, CalcularIss: newValue };
    this.issUpdating = true;

    // O toggle vale para o lote todo: as retenções de todos os sublotes são reapuradas.
    this.batch.Partners?.forEach((partner) => this.recalculatingBatchIds.add(partner.BatchId.toString()));

    this._batchService.postCalcularIss(batchId, newValue).subscribe({
      next: () => {
        this.issUpdating = false;
        // O ISS de PF depende do toggle: recarrega para os chips de retenção e o líquido do card refletirem.
        this.getBatchDetails(true);
        this._snackbarService.openSnackBar('Configuração de ISS atualizada com sucesso!', 'success', 3000);
      },
      error: (error) => {
        this.recalculatingBatchIds.clear();
        this.batch = { ...this.batch, CalcularIss: previousValue };
        this.issUpdating = false;
        const message = error?.error?.Message || 'Erro ao atualizar a configuração de ISS. Por favor, tente novamente.';
        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  handleServiceCodeChange(event: { batchId: string; serviceCodeId: string | null }): void {
    // Só as retenções do sublote alterado viram skeleton (o restante da tela permanece).
    this.recalculatingBatchIds.add(event.batchId);

    this._batchService.postServiceCode(event.batchId, event.serviceCodeId).subscribe({
      // Servidor é a fonte única do líquido: recarrega o lote (em segundo plano, sem refresh da tela) em vez
      // de recalcular no cliente.
      next: () => {
        this.getBatchDetails(true);
        this._snackbarService.openSnackBar('Código de serviço atualizado com sucesso!', 'success', 3000);
      },
      error: (error) => {
        // Recarrega para o seletor do card voltar ao valor realmente gravado.
        this.getBatchDetails(true);
        // O interceptor já converte o 400 em Error(message); sem ele o corpo vem como {Message} ou {message}.
        const message = error?.error?.Message || error?.error?.message || error?.message || 'Erro ao atualizar o código de serviço. Por favor, tente novamente.';
        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  checkIntegrationSap(): void {
    if (
      this.batch.StatusOfOnlending?.toLowerCase().trim() !== 'cancelado' &&
      (this.batch.StatusOfOnlending?.toLowerCase().trim() === 'aprovado' ||
        (this.batch.Partners!.length > 0 &&
          (this.batchIsInvoiced || this.batchHasRpa) &&
          this.batch.StatusOfOnlending?.toLowerCase().trim() !== 'liberado') ||
        this.batch.IntegrationError)
    ) {
      this.allowIntegrationSap = true;
      this.allowLiberacaoManual = this.typeDetailLot === DetailLotEnum.Transfer;
    } else {
      this.allowIntegrationSap = false;
      this.allowLiberacaoManual = false;
    }

    if (this.batch.StatusOfOnlending?.toLowerCase().trim() === 'liberado manual') {
      this.allowIntegrationSap = false;
      this.allowLiberacaoManual = false;
    }

    if (this.isMasterManualReleased()) {
      this.allowIntegrationSap = false;
      this.allowLiberacaoManual = false;
    }

    if (this.batch.BatchStatus?.toLowerCase().trim() === 'faturado') {
      this.integratedOnSAP = true;
    }
  }

  checkBatchStatus(): void {
    let blockBatch = false;

    if (this.batch.Partners != null) {
      const masterBatch = this.batch.Partners?.filter(p => p.FirstContractIsSubstabelecido == false)[0];
      if (masterBatch != null) {
        blockBatch = masterBatch.PaymentDate != null ||
          (masterBatch.Integrated == true && masterBatch.IntegrationDate != null);
      }
    }

    if (this.isMasterManualReleased()) {
      blockBatch = true;
    }

    const status = this.batch.StatusOfOnlending?.toLowerCase().trim();
    const hasSubBatchBlockedForCancel = (this.batch.Partners ?? []).some(partner => this.isBlockedForCancel(partner));

    this.allowCancelBatch = status !== 'cancelado' && !hasSubBatchBlockedForCancel;
    this.allowReturnStatus = status === 'cancelado';
    this.batchBlocked = status === 'cancelado' || blockBatch;

    this.integratedSubestabelecidoContractIds = new Set(
      (this.batch.Partners ?? [])
        .filter(p => p.FirstContractIsSubstabelecido == true && p.Integrated == true && p.IntegrationDate != null)
        .flatMap(p => (p.Contracts ?? []).map(c => c.ContractId))
    );

    this.showRpaButton = this.allBatchsAreCpf();
    this.disableSendTransferBatchEmail = this.showRpaButton && this.anyBatchHaveRPA();
  }

  private isMasterManualReleased(): boolean {
    const masterBatch = this.batch?.Partners?.find(p => p.FirstContractIsSubstabelecido == false);

    if (!masterBatch) {
      return false;
    }

    const status = (masterBatch.StatusOfOnlending || masterBatch.BatchStatus || '').toLowerCase().trim();
    const sapStatus = (masterBatch.SapPaymentStatus || '').toLowerCase().trim();

    return status === 'liberado manual' || sapStatus === 'manual';
  }

  private isBlockedForCancel(partnerBatch: ILot): boolean {
    const status = (partnerBatch.StatusOfOnlending || partnerBatch.BatchStatus || '').toLowerCase().trim();
    const isReleased = status === 'liberado' || status === 'liberado manual';
    const isIntegrated = partnerBatch.Integrated === true || !!partnerBatch.IntegrationDate;

    return isReleased || isIntegrated;
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    const apiError = err?.error;

    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }

    return apiError?.Message
      || apiError?.message
      || apiError?.title
      || err?.message
      || fallback;
  }

  listenInvoiceChange(): void {
    this.invoiceChangeSubscription = this._batchService.updateTemplateValuesEvent.subscribe({
      next: () => {
        this.getBatchDetails();
      }
    });

    this.rpaChangeSubscription = this._batchService.updateTemplateValuesEventRpa.subscribe({
      next: () => {
        this.getBatchDetails();
      }
    });
  }

  handleDeleteContract(result: { contractId: string; isDelete: boolean }): void {
    const { contractId, isDelete } = result;
    if (isDelete) {
      if (!this.selectedContractIdsToExclude.has(contractId)) {
        this.contractsToExclude.push(contractId);
        this.selectedContractIdsToExclude.add(contractId);
      }
    } else {
      const index = this.contractsToExclude.indexOf(contractId);
      if (index >= 0) {
        this.contractsToExclude.splice(index, 1);
      }
      this.selectedContractIdsToExclude.delete(contractId);
    }
  }

  openDeleteContractsModal(): void {
    const text: string = this.contractsToExclude.length > 1 ? 'contratos' : 'contrato';
    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Excluir contratos',
        title: `Deseja excluir (${this.contractsToExclude.length}) ${text} deste lote?`,
        confirm: 'Sim',
        dismiss: 'Não'
      }
    };
    this._dialogService.openDialog(dialogData).then((response) => {
      if (response) {
        this.deleteContracts();
      }
    });
  }

  deleteContracts(): void {
    const contractIds: IContractIds = {
      BatchId: this.batchId,
      ContractIds: [...this.contractsToExclude]
    };
    this._manualBatchService.deleteContractsManualBatch(contractIds).subscribe({
      next: () => {
        this.contractsToExclude = [];
        this.selectedContractIdsToExclude.clear();
        this._snackbarService.openSnackBar('Contrato(s) excluído(s) com sucesso!', 'success', 3000);
        this.getBatchDetails();
      },
      error: (err) => {
        const message = this.getApiErrorMessage(
          err,
          'Erro ao excluir contrato(s). Por favor, tente novamente.'
        );

        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  get batchIsInvoiced(): boolean {
    return (
      this.batch.IsInvoiced ||
      (this.batch.Partners?.filter(p => p.AmountTotal > 0).every(p => p.IsInvoiced) as boolean)
    );
  }

  get batchHasRpa(): boolean {
    return (
      this.batch.HasRpa ||
      (this.batch.Partners?.filter(p => p.AmountTotal > 0).every(p => p.HasRpa) as boolean)
    );
  }

  integrateSap(): void {
    this._manualBatchService.integrateManualTransferWithSap(this.batchId.toString()).subscribe({
      next: () => {
        this._snackbarService.openSnackBar('Integração realizada com sucesso!', 'success');
        this.getBatchDetails();
      }
    });
  }

  liberarManualEvent(evento: { payload: IManualBatchLiberacao }): void {
    if (!this.batchId) {
      this._snackbarService.openSnackBar('ID do lote não encontrado', 'danger');
      return;
    }
    if (!evento?.payload) {
      this._snackbarService.openSnackBar('Dados de liberação inválidos', 'danger');
      return;
    }
    this._manualBatchService
      .liberarManualTransferBatch(evento.payload.BatchId, evento.payload)
      .subscribe({
        next: () => {
          this._snackbarService.openSnackBar('Liberação manual realizada com sucesso!', 'success', 3000);
          this.getBatchDetails();
        },
        error: (err: any) => {
          const apiMessage = this.getApiErrorMessage(
            err,
            'Erro ao realizar liberação manual. Por favor, tente novamente.'
          );

          this._snackbarService.openSnackBar(
            apiMessage,
            'danger',
            5000
          );
        }
      });
  }

  integrateSapPartner(batchId: string): void {
    this._lotDetailService.integrateLotTransferWithSap(batchId).subscribe({
      next: () => {
        this._snackbarService.openSnackBar('Integração realizada com sucesso!', 'success');
        this.getBatchDetails();
      }
    });
  }

  getBatchEventLogs(): void {
    const observers = forkJoin([
      this._batchService.getTransferBatchEventLog(this.batch.BatchNumber, this.batch.BatchId),
      this._batchService.getTransferBatchEventSapLog(this.batch.BatchId)
    ]);

    observers.subscribe({
      next: ([logs, logsSap]) => {
        this._dialogService.openBatchEventLogDialog(logs.Data, logsSap.Data).then(() => { });
      }
    });
  }

  cancelSubBatch(payload: { batchId: string; justificativa: string }): void {
    this._manualBatchService
      .cancelManualBatchPosIntegracao(payload.batchId, { Justificativa: payload.justificativa })
      .subscribe({
        next: () => {
          this._snackbarService.openSnackBar('Integração com o OMIE cancelada com sucesso!', 'success', 3000);
          this.getBatchDetails();
        }
      });
  }

  handleSendingTransferBatchEmail(paymentEmail: boolean, partnerId?: string): void {
    const textSubject: string = partnerId ? 'beneficiário' : 'lote';
    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Enviar e-mail',
        title: `Caso prossiga, o disparo de e-mail será realizado para este ${textSubject}. Deseja continuar?`,
        confirm: 'Sim',
        dismiss: 'Não'
      }
    };
    this._dialogService.openDialog(dialogData).then((response) => {
      if (response) {
        if (paymentEmail) {
          this.sendTransferEstimatedPaymentDateBatchEmail(partnerId);
        } else {
          if (this.showRpaButton) {
            const dialogRef = this._dialog.open(ModalMsgRpaV2Component, { disableClose: true });
            dialogRef.afterClosed().subscribe((result: any) => {
              if (result.save) {
                this.sendTransferBatchEmail(result.msg);
              }
            });
          } else {
            this.sendTransferBatchEmail(undefined, partnerId);
          }
        }
      }
    });
  }

  /**
   * Mostra o que "E-mail Lote" / "E-mail Pgto" enviaria (um item por sublote),
   * montado pela API com os dados reais. Não envia e não grava histórico.
   * O modal abre na hora com as abas dos sublotes e carrega cada prévia ali.
   */
  openEmailPreview(paymentEmail: boolean, partnerId?: string): void {
    const batchId: string = partnerId ?? this.batch.BatchId.toString();
    const sublotes: ILot[] = partnerId
      ? (this.batch.Partners ?? []).filter((partner) => partner.BatchId.toString() === partnerId)
      : (this.batch.Partners ?? []).filter((partner) => partner.Active !== false);
    const data: IModalPreviewEmailData = {
      title: paymentEmail ? 'Prévia do e-mail de pagamento' : 'Prévia do e-mail do lote',
      paymentEmail,
      batchId,
      tabs: sublotes.map((partner) => ({
        BatchId: partner.BatchId.toString(),
        BatchNumber: partner.BatchNumber,
        BeneficiaryName: partner.Partner
      }))
    };

    this._dialog.open(ModalPreviewEmailV2Component, { width: '1000px', maxWidth: '95vw', data });
  }

  sendTransferBatchEmail(userMsg?: string, partnerId?: string): void {
    this._batchService
      .sendTransferBatchEmail(partnerId || this.batch.BatchId.toString(), userMsg)
      .subscribe({
        next: () => {
          this._snackbarService.openSnackBar('Disparo de e-mail realizado com sucesso!', 'success');
        }
      });
  }

  sendTransferEstimatedPaymentDateBatchEmail(partnerId?: string): void {
    const batchId: string = partnerId ?? this.batch.BatchId.toString();
    this._batchService.sendTransferEstimatedPaymentBatchEmail(batchId).subscribe({
      next: (response) => {
        if (response.allBatchsSent) {
          this._snackbarService.openSnackBar('Disparo de e-mail realizado com sucesso!', 'success');
        } else {
          this._snackbarService.openSnackBar('Alguns lotes não foram enviados!', 'warning');
        }
        this.getBatchDetails();
      }
    });
  }

  handleCancelBatchEvent(): void {
    this._manualBatchService.cancelManualBatch(this.batchId).subscribe({
      next: () => {
        this._snackbarService.openSnackBar('Lote cancelado com sucesso!', 'success', 3000);
        this.getBatchDetails();
      }
    });
  }

  handleReturnBatchStatusEvent(): void {
    this._manualBatchService.returnStatusManualBatch(this.batchId).subscribe({
      next: () => {
        this._snackbarService.openSnackBar('Lote atualizado com sucesso!', 'success', 3000);
        this.getBatchDetails();
      }
    });
  }

  allBatchsAreCpf(): boolean {
    if (this.batch && this.batch.Partners) {
      return this.batch.Partners.every(
        x => x.PartnerDocument && DocumentValidator.isCPF(x.PartnerDocument)
      );
    }
    return false;
  }

  anyBatchHaveRPA(): boolean {
    if (this.batch && this.batch.Partners) {
      return this.batch.Partners.some(x => x.HasRpa == true);
    }
    return false;
  }

  getStatusClass(status: string | undefined): string {
    const s = (status || '')
      .toLowerCase()
      .trim()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '');

    if (!s || s === '-') return 'badge-neutral';

    if (['faturado', 'refaturado', 'pago', 'integrado', 'concluido', 'aprovado', 'liberado', 'liberado parcialmente'].includes(s)) {
      return 'badge-success';
    }
    if (['em andamento', 'andamento', 'processando', 'aguardando', 'aguardando aprovacao'].includes(s)) {
      return 'badge-info';
    }
    if (['revertido', 'reverso', 'reversed', 'estornado', 'cancelado', 'erro'].includes(s)) {
      return 'badge-danger';
    }

    return 'badge-warn';
  }
}
